import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { quietToastOptions } from '@/lib/toast-chrome'
import {
  detectAppUpdateChange,
  fetchAppVersionSnapshot,
  type AppVersionSnapshot,
} from '@/lib/app-update'

const POLL_MS = 3 * 60 * 1000
const TOAST_ID = 'maater-app-update'

/**
 * Poll frontend /version.json + API /health/live. When either deploy fingerprint
 * changes after the session baseline, toast once with a Reload action.
 */
export function useAppUpdateCheck(enabled = true): void {
  const baselineRef = useRef<AppVersionSnapshot | null>(null)
  const toastedRef = useRef(false)

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return

    let cancelled = false
    let timer: ReturnType<typeof setInterval> | null = null

    const showReloadToast = (frontendChanged: boolean, apiChanged: boolean) => {
      if (toastedRef.current) return
      toastedRef.current = true
      const what =
        frontendChanged && apiChanged
          ? 'App and server'
          : frontendChanged
            ? 'App'
            : 'Server'
      toast.info(`${what} update ready`, quietToastOptions('info', {
        id: TOAST_ID,
        description: 'Reload to get the latest Maater.',
        duration: Infinity,
        action: {
          label: 'Reload',
          onClick: () => {
            window.location.reload()
          },
        },
      }))
    }

    const check = async () => {
      if (cancelled || toastedRef.current) return
      const snap = await fetchAppVersionSnapshot()
      if (cancelled) return

      if (!baselineRef.current) {
        baselineRef.current = snap
        return
      }

      const change = detectAppUpdateChange(baselineRef.current, snap)
      if (change.frontendChanged || change.apiChanged) {
        showReloadToast(change.frontendChanged, change.apiChanged)
      }
    }

    void check()
    timer = setInterval(() => {
      void check()
    }, POLL_MS)

    const onVisible = () => {
      if (document.visibilityState === 'visible') void check()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [enabled])
}
